<script setup lang="ts">
import SearchField from '@/components/SearchField.vue'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import { useI18n } from 'vue-i18n'
import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import type { DataTableHeader } from 'vuetify'
import { errToStr } from '@/helpers/tools'
import { Liegenschaft, LiegenschaftCollection } from '@/models/Liegenschaft'
import { useRouter } from 'vue-router'
import type { ItemSlotBase } from 'vuetify/lib/components/VDataTable/types.mjs'
import { useToast } from '@/composables/toast'

type HeaderWidthType = {
    adresse: string
    plz: string
    ort: string
    gemeinde: string
    action: string
}

const {
    addbtn = null,
    edit = true,
    dense = false,
    nofooter = false,
    selectable = false,
    height = undefined,
    selection = null,
    headerwidths = {
        adresse: '40%',
        plz: '10%',
        ort: '20%',
        gemeinde: '20%',
        action: '120px',
    },
    gesuchId = null,
} = defineProps<{
    addbtn?: string
    edit?: boolean
    dense?: boolean
    nofooter?: boolean
    selectable?: boolean
    height?: number
    selection?: Liegenschaft | null
    headerwidths?: HeaderWidthType
    gesuchId?: number
}>()

const router = useRouter()
const liegenschaften = new LiegenschaftCollection()
const recLoading = ref(true)
const calculateWidth = ref(true)
const searchString = ref('')
const total = ref(0)
const selectedItems: Ref<Liegenschaft[]> = ref([])
const confirmDelDlg = ref(false)
const toDelete: Ref<Liegenschaft | null> = ref(null)
const options = ref({
    page: 1,
    itemsPerPage: 50,
    sortBy: ['adresse'],
    sortDesc: [false],
    multiSort: false,
    mustSort: true,
})

const emit = defineEmits<{
    (e: 'reload'): void
    (e: 'new-liegenschaft'): void
    (e: 'selection', item: Liegenschaft | null): void
}>()

const { t } = useI18n()
const headers: ComputedRef<DataTableHeader[]> = computed(() => {
    const res: DataTableHeader[] = [
        {
            title: t('adresse'),
            value: '$.adresse',
            sortable: true,
            width: headerwidths.adresse,
        },
        {
            title: t('plz'),
            value: '$.ort_plz',
            sortable: true,
            width: headerwidths.plz,
        },
        {
            title: t('ort'),
            value: '$.ort',
            sortable: true,
            width: headerwidths.ort,
        },
        {
            title: t('gemeinde'),
            value: '$.gemeinde_bezeichnung',
            sortable: true,
            width: headerwidths.ort,
        },
    ]
    if (edit) {
        res.push({
            value: 'action',
            align: 'end',
            width: headerwidths.action,
            minWidth: headerwidths.action,
            sortable: false,
        })
    }
    return res
})

watch(
    () => selection,
    (val: Liegenschaft | Liegenschaft[] | null) => {
        if (val) {
            if (!Array.isArray(val)) {
                val = [val]
            }
            selectedItems.value = val
        }
    },
)

watch(selectedItems, () => {
    emitSelection()
})

async function getLiegenschaften({
    page,
    itemsPerPage,
    sortBy,
}: {
    page: number
    itemsPerPage: number
    sortBy: string[]
}) {
    recLoading.value = true
    try {
        liegenschaften.setQueryParam('gesuchId', gesuchId)
        liegenschaften.setQueryParam('itemsPerPage', itemsPerPage)
        liegenschaften.setQueryParam('page', page)
        liegenschaften.setQueryParam('sortBy', sortBy)
        liegenschaften.setQueryParam('query', searchString.value)
        await liegenschaften.query()
        total.value = liegenschaften.getServerTotal()
    } catch (e) {
        console.log(e)
    } finally {
        recLoading.value = false
    }
}
function executeSearch(value: string) {
    searchString.value = value
}
function showForm(item: Liegenschaft) {
    router.push({ name: 'liegenschaft_edit', params: { id: item.id || 'new' } })
}
function onRowClicked(event: PointerEvent, row: ItemSlotBase<Liegenschaft>) {
    if (selectable) {
        selectedItems.value = [row.item]
    } else {
        showForm(row.item)
    }
}
function emitSelection() {
    if (selectedItems.value.length === 1) {
        emit('selection', selectedItems.value[0])
    } else {
        emit('selection', null)
    }
}
function destroyItem(item: Liegenschaft) {
    toDelete.value = item
    confirmDelDlg.value = true
}
async function confirmDestroy() {
    if (toDelete.value) {
        try {
            await toDelete.value?.destroy()
            liegenschaften.remove(toDelete.value)
            toDelete.value = null
            total.value = total.value - 1
            confirmDelDlg.value = false
        } catch (e) {
            useToast().add({ text: errToStr(e), color: 'error' })
        }
    }
}
</script>

<template>
    <v-data-table-server
        v-model:model-value="selectedItems"
        :class="{
            'fixed-table': true,
            'fixed-overflow-table': !dense,
            'row-pointer': true,
        }"
        :headers="headers"
        :calculate-widths="calculateWidth"
        :items="liegenschaften.getModels()"
        :v-model:options="options"
        :items-length="total"
        :items-per-page="50"
        :loading="recLoading"
        :dense="dense"
        :search="searchString"
        select-strategy="single"
        item-key="id"
        return-object
        :show-select="selectable"
        :hide-default-footer="nofooter"
        :no-data-text="$t('no_records')"
        :height="height"
        :footer-props="{
            itemsPerPageOptions: [10, 25, 50, 100, -1],
        }"
        @click:row="onRowClicked"
        @update:options="getLiegenschaften"
    >
        <template v-slot:top>
            <div class="d-flex align-end justify-end">
                <SearchField
                    class="white--text"
                    @executeSearch="executeSearch"
                    max-width="500px"
                    :width="addbtn ? '65vw' : '90vw'"
                ></SearchField>
                <v-btn
                    v-if="addbtn"
                    variant="flat"
                    color="action white--text"
                    class="hidden-sm-and-up add-button"
                    justify-center
                    rounded="xl"
                    icon="fas fa-plus"
                    size="small"
                    @click="$emit('new-liegenschaft')"
                >
                </v-btn>
                <v-btn
                    v-if="addbtn"
                    variant="flat"
                    color="action white--text"
                    class="hidden-xs add-button"
                    justify-center
                    rounded
                    prepend-icon="fas fa-plus fa-xs"
                    @click="$emit('new-liegenschaft')"
                >
                    {{ addbtn }}
                </v-btn>
            </div>
        </template>
        <template v-slot:[`item.action`]="{ item }">
            <v-btn
                class="mr-1"
                size="x-small"
                variant="text"
                color="accent"
                icon="fas fa-pen"
                @click.stop="showForm(item)"
            >
            </v-btn>
            <v-btn
                class="mr-1"
                size="x-small"
                variant="text"
                color="error"
                icon="fas fa-trash"
                @click.stop="destroyItem(item)"
            >
            </v-btn>
        </template>
    </v-data-table-server>
    <ConfirmDlg
        v-model:model-value="confirmDelDlg"
        :title="$t('bestaetigung')"
        :text="$t('really_delete_rec')"
        @yes="confirmDestroy"
        @no="confirmDelDlg = false"
    />
</template>

<style scoped>
.add-button {
    margin-left: 1rem;
    align-self: center;
}
.fixed-table {
    :deep(table) {
        table-layout: fixed;
        width: 100%;
    }
}
.row-pointer {
    :deep(tbody tr :hover) {
        cursor: pointer;
    }
}
@media screen and (min-width: 600px) {
    .fixed-table {
        :deep(table td) {
            text-overflow: ellipsis;
        }
    }
    .fixed-overflow-table {
        :deep(table td) {
            overflow: hidden;
        }
    }
}
@media screen and (max-width: 600px) {
    .fixed-table {
        :deep(table th) {
            width: 100%;
        }
    }
}
</style>
